// frontend/src/pages/TicketsPage.jsx — CS 티켓(선개발, 사이드바 미노출).
// /tickets 직접 접근으로 미리보기. collab_platform.md §18. MM 승격·Notion 자동이관은 후속(외부 연동).
import { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/AppShell';
import Markdown from '../components/Markdown';
import { ticketAPI } from '../api/client';
import { safeUrl } from '../utils/safeUrl';
import useIsMobile from '../hooks/useIsMobile';
import { IconAlertTriangle, IconArrowLeft, IconExternalLink, IconPlus, IconRefresh, IconRobot, IconTicket } from '@tabler/icons-react';
import { card as cardBase, input as inputBase, btn, SHADOW, ICON_STROKE } from '../utils/uiStyles';
import { Page, PageHeader, Button, PillTabs, SearchInput, Pill, Tag, Avatar, MetricTile, EmptyState, LoadingState, ErrorState } from '../components/ui';
import { alertDialog, confirmDialog, toast } from '../components/AppDialog';

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// 메신저 추가 정보 첨부 — 이미지는 인라인 미리보기(클릭=다운로드), 그 외는 다운로드 칩.
// <img src>로는 Authorization 헤더가 안 실리므로 axios blob → objectURL 로 우회.
function MmAttachment({ tid, f }) {
  const isImg = (f.mime || '').startsWith('image/');
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let obj = null;
    if (isImg) {
      ticketAPI.mmFile(tid, f.id).then((r) => { if (!alive) return; obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    }
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [tid, f.id, isImg]);
  const download = async () => {
    try {
      const r = await ticketAPI.mmFile(tid, f.id);
      const obj = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = obj; a.download = f.name || 'file'; a.click();
      setTimeout(() => URL.revokeObjectURL(obj), 4000);
    } catch (e) { alertDialog('첨부를 가져오지 못했습니다.'); }
  };
  if (isImg && url) {
    return <img src={url} alt={f.name} title={f.name + ' (클릭=다운로드)'} onClick={download} style={{ maxWidth: '100%', maxHeight: '260px', borderRadius: '8px', border: '1px solid var(--gw-line, #eeeeec)', cursor: 'pointer', display: 'block' }} />;
  }
  return (
    <Button variant="secondary" size="sm" onClick={download}>
      📎 {f.name || f.id}{f.size ? ` (${Math.round(f.size / 1024)}KB)` : ''}
    </Button>
  );
}

// 색은 TONES 키(tone) — 그리는 쪽이 components/ui 의 Pill·Tag 다. 우선순위는 글자색만.
const STATUS = { open: { label: '접수', tone: 'blue' }, in_progress: { label: '처리중', tone: 'yellow' }, done: { label: '완료', tone: 'green' } };
const PRIO = { urgent: { label: '긴급', color: 'var(--gw-red-ink, #a4564d)' }, high: { label: '높음', color: 'var(--gw-orange, #d28b50)' }, normal: { label: '보통', color: 'var(--gw-secondary, #64635f)' }, low: { label: '낮음', color: 'var(--gw-muted, #92918d)' } };
const HANDLING = { self: '자체처리', dev: '개발사이관' };
const STATUS_KEYS = ['open', 'in_progress', 'done'];
const PRIO_KEYS = ['urgent', 'high', 'normal', 'low'];


// 문의 이력 — 메신저 `/cs` 로 들어온 **모든 문의**(해결·무반응 포함).
// 티켓 목록은 '접수(escalated)'된 것만이라, AI 1차답변으로 해결된 건은 여기서만 보인다.
// 목적이 "질문 ↔ AI 답변"을 읽는 것이라 행을 눌러 **답변 전문을 펼쳐** 본다.
const OUTCOME = {
  resolved:  { label: '✅ AI 답변으로 해결', tone: 'green' },
  escalated: { label: '📋 접수(티켓)', tone: 'yellow' },
  answered:  { label: '⏳ 응답 없음', tone: 'gray' },
  pending:   { label: '… 생성 중', tone: 'blue' },
};

function IntakeList({ intakes, counts, loading, error, fOutcome, setFOutcome, search, setSearch,
                      open, setOpen, Inp, mobile, onReload, onOpenTicket }) {
  const tot = (counts.answered || 0) + (counts.resolved || 0) + (counts.escalated || 0) + (counts.pending || 0);
  return (
    <>
      {error && (
        <div style={{ marginBottom: '12px' }}><ErrorState message={error} /></div>
      )}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
        <select value={fOutcome} onChange={(e) => setFOutcome(e.target.value)} style={{ ...Inp, width: 'auto', height: '30px' }}>
          <option value="">결과 전체 ({tot})</option>
          <option value="resolved">✅ AI 답변으로 해결 ({counts.resolved || 0})</option>
          <option value="escalated">📋 접수(티켓) ({counts.escalated || 0})</option>
          <option value="answered">⏳ 응답 없음 ({counts.answered || 0})</option>
          <option value="pending">… 생성 중 ({counts.pending || 0})</option>
        </select>
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="질문·답변 검색" width={mobile ? '100%' : '200px'} />
        {(fOutcome || search) && (
          <Button variant="quiet" onClick={() => { setFOutcome(''); setSearch(''); }}>필터 초기화</Button>
        )}
        <Button variant="secondary" icon={IconRefresh} onClick={onReload} style={{ marginLeft: mobile ? 0 : 'auto' }}>새로고침</Button>
      </div>
      <p style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)', marginBottom: '10px', lineHeight: 1.6 }}>
        메신저에서 <b>/cs</b> 로 올라온 문의와 <b>AI 1차 답변</b>입니다. 행을 누르면 답변 전문과 참고한 과거 사례가 펼쳐집니다.
        <b>티켓 목록에는 &apos;접수&apos;된 건만</b> 올라오므로, AI 답변으로 해결된 문의는 이 탭에서만 볼 수 있습니다.
      </p>
      <div style={{ ...cardBase, padding: 0, overflow: 'hidden' }}>
        {loading && <LoadingState>문의를 불러오는 중입니다.</LoadingState>}
        {!loading && intakes.length === 0 && (
          <EmptyState description={(fOutcome || search) ? '조건에 맞는 문의가 없습니다.' : '아직 접수된 문의가 없습니다.'} />
        )}
        {!loading && intakes.map((it, i) => {
          const oc = OUTCOME[it.outcome] || OUTCOME.answered;
          const opened = open === it.id;
          return (
            <div key={it.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--gw-line, #eeeeec)' }}>
              <div onClick={() => setOpen(opened ? null : it.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer', flexWrap: mobile ? 'wrap' : 'nowrap' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gw-subtle, #fbfbfa)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ flexShrink: 0 }}><Pill tone={oc.tone}>{oc.label}</Pill></span>
                {it.system && <span style={{ flexShrink: 0 }}><Tag tone="purple">{it.system}</Tag></span>}
                <span style={{ flex: 1, minWidth: 0, fontSize: '14px', color: 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.question}</span>
                {it.rounds > 1 && <span title="추가 정보로 재판단한 횟수" style={{ fontSize: '11px', color: 'var(--gw-purple-ink, #7c5e96)', flexShrink: 0 }}>재판단 {it.rounds}</span>}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0, fontSize: '12px', color: 'var(--gw-secondary, #64635f)' }}>{it.requester_name && <Avatar name={it.requester_name} size={19} />}{it.requester_name}</span>
                <span style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', flexShrink: 0, width: '78px', textAlign: 'right' }}>{fmtDate(it.created_at)}</span>
              </div>
              {opened && (
                <div style={{ padding: '4px 16px 18px', background: 'var(--gw-subtle, #fbfbfa)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gw-secondary, #64635f)', margin: '8px 0 4px' }}>질문</div>
                  <div style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{it.question}</div>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gw-secondary, #64635f)', margin: '14px 0 4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <IconRobot size={14} stroke={ICON_STROKE} />AI 1차 답변
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', whiteSpace: 'pre-wrap', lineHeight: 1.7, background: 'var(--gw-surface, #fff)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '8px', padding: '12px' }}>
                    {/* 접수 즉시 행이 생기므로 답변이 비어 있다고 '못 찾음'이 아니다 — 생성 중일 수 있다. */}
                    {it.answer
                      || (it.outcome === 'pending'
                        ? '⏳ 답변을 만들고 있습니다…'
                        : '(유사 사례를 찾지 못해 답변을 만들지 않았습니다)')}
                  </div>
                  {it.rag_refs && (
                    <div style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)', marginTop: '10px', lineHeight: 1.6 }}>
                      <b>참고한 과거 사례</b> — {it.rag_refs}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', marginTop: '10px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <span>결과: {oc.label}{it.actor ? ` (${it.actor})` : ''}</span>
                    {it.ticket_id && (
                      <button onClick={(e) => { e.stopPropagation(); onOpenTicket(it.ticket_id); }}
                        style={{ border: 'none', background: 'none', padding: 0, color: 'var(--gw-blue, #2383e2)', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>
                        접수된 티켓 #{it.ticket_id} 열기
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

const empty = { title: '', content: '', shipper: '', center: '', type: '', system: '', priority: 'normal', handling: '', assignee_name: '', status: 'open', notion_url: '' };

export default function TicketsPage() {
  const mobile = useIsMobile();
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [fStatus, setFStatus] = useState('');
  const [fHandling, setFHandling] = useState('');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [notionSet, setNotionSet] = useState(false);
  const [notionInput, setNotionInput] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [aiSug, setAiSug] = useState(null);
  // 문의 이력(마스터 2026-08-18): 티켓 목록엔 '접수(escalated)'된 것만 올라온다.
  // 해결·무반응 건은 cs_intakes 에만 남아 화면에서 볼 수 없었다 —
  // "무슨 질문이 올라왔고 AI가 어떻게 대답해서 해결됐는지" 보려면 이 탭이 필요하다.
  const [view, setView] = useState('tickets');          // tickets | intakes
  const [intakes, setIntakes] = useState([]);
  const [intakeCounts, setIntakeCounts] = useState({});
  const [intakeError, setIntakeError] = useState('');
  const [fOutcome, setFOutcome] = useState('');
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [openIntake, setOpenIntake] = useState(null);   // 펼친 문의 id
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);
  const [notionBusy, setNotionBusy] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const isAdmin = me.role === 'admin';
  const hasFilter = !!(fStatus || fHandling || search);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const listReq = ticketAPI.list({ status: fStatus || undefined, handling: fHandling || undefined, q: search || undefined })
      .then((r) => setTickets(r.data.tickets || []))
      .catch(() => { setError('티켓 목록을 불러오지 못했습니다.'); })
      .finally(() => setLoading(false));
    ticketAPI.stats().then((r) => setStats(r.data || {})).catch(() => {});
    return listReq;
  }, [fStatus, fHandling, search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);   // 검색 디바운스
  const loadIntakes = useCallback(() => {
    setIntakeLoading(true);
    ticketAPI.intakes({ outcome: fOutcome || undefined, q: search || undefined })
      .then((r) => { setIntakes(r.data?.intakes || []); setIntakeCounts(r.data?.counts || {}); setIntakeError(''); })
      // 403 을 빈 목록으로 삼키면 "문의가 하나도 없네"로 읽힌다(2026-08-28 검수). 문의 원문엔
      // 조회 결과(화주·주문)가 섞이므로 조회권(db_access)까지 요구하게 바뀌었다 — 그 사실을 말해준다.
      .catch((e) => {
        setIntakes([]); setIntakeCounts({});
        setIntakeError(e?.response?.status === 403
          ? '문의 원문에는 조회 결과(화주·주문 정보)가 섞일 수 있어 DB 조회 권한이 있는 계정만 볼 수 있습니다. 관리자에게 요청해 주세요.'
          : '문의 목록을 불러오지 못했습니다.');
      })
      .finally(() => setIntakeLoading(false));
  }, [fOutcome, search]);
  useEffect(() => {
    if (view !== 'intakes') return undefined;
    const t = setTimeout(loadIntakes, 300);
    return () => clearTimeout(t);
  }, [view, loadIntakes]);
  useEffect(() => { ticketAPI.getConfig().then((r) => setNotionSet(!!r.data.notion_db_set)).catch(() => {}); }, []);

  // 추가 정보(메신저 답글)는 백그라운드로 유입되므로, 열려 있는 상세는 5초 폴링으로 자동 갱신
  // (새로고침 없이 보이게 — 마스터 2026-08-14). 내용이 같으면 상태를 안 바꿔 불필요한 리렌더 방지.
  const activeId = active ? active.id : null;
  // 티켓 전환 시 입력 상태 초기화 — 답변 초안이 다른 티켓의 메신저 스레드로 발송되는 사고 방지(전수조사)
  useEffect(() => { setReplyText(''); setAiSug(null); }, [activeId]);
  useEffect(() => {
    if (!activeId) return undefined;
    const timer = setInterval(() => {
      if (document.hidden) return;   // 숨은 탭에서는 폴링 정지
      ticketAPI.get(activeId).then((r) => setActive((cur) => {
        if (!cur || cur.id !== activeId) return cur;
        return JSON.stringify(cur) === JSON.stringify(r.data) ? cur : r.data;
      })).catch((e) => {
        if (e.response?.status === 404) setActive((cur) => (cur && cur.id === activeId ? null : cur));
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [activeId]);

  const saveNotion = async () => {
    setNotionBusy(true);
    try { const r = await ticketAPI.setConfig({ notion_db_id: notionInput.trim() }); setNotionSet(!!r.data.notion_db_set); setNotionInput(''); toast(r.data.notion_db_set ? 'Notion 이관 DB를 설정했습니다.' : 'DB 설정을 해제했습니다.'); }
    catch (e) { alertDialog('저장 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setNotionBusy(false); }
  };
  const handoff = async (t) => {
    if (handoffBusy) return;
    if (!(await confirmDialog('이 티켓을 개발사 문의(Notion)로 이관하시겠습니까?'))) return;
    setHandoffBusy(true);   // 노션 페이지 생성+추가정보(코멘트·첨부) 전달이라 수 초~수십 초 걸림
    try { const r = await ticketAPI.handoff(t.id); ticketAPI.get(t.id).then((rr) => setActive((cur) => (cur && cur.id === t.id ? rr.data : cur))).catch(() => {}); load();
      // 속성으로 못 실은 값(예: 보드에 없는 신규 화주사)은 실행자가 바로 알아야 손으로 채운다.
      const warns = r.data.warnings || [];
      alertDialog((warns.length
        ? '⚠️ 이관은 됐지만 일부 항목이 비어 있습니다.\n\n' + warns.join('\n')
        : '이관 완료') + (r.data.notion_url ? '\n\n' + r.data.notion_url : '')); }
    catch (e) { alertDialog('이관 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setHandoffBusy(false); }
  };
  const runAi = async (t) => {
    setAiBusy(true); setAiSug(null);
    try {
      const r = await ticketAPI.aiAssist(t.id);
      setAiSug(r.data);
      ticketAPI.get(t.id).then((rr) => setActive((cur) => (cur && cur.id === t.id ? rr.data : cur))).catch(() => {});  // ai_summary 갱신
    } catch (e) { alertDialog('AI 분석 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setAiBusy(false); }
  };
  const applyAi = async (t) => {
    if (!aiSug) return;
    const patch = {};
    ['handling', 'priority', 'type', 'shipper', 'center'].forEach((k) => { if (aiSug[k]) patch[k] = aiSug[k]; });
    try { await ticketAPI.update(t.id, patch); ticketAPI.get(t.id).then((rr) => setActive((cur) => (cur && cur.id === t.id ? rr.data : cur))).catch(() => {}); load(); setAiSug(null); }
    catch (e) { alertDialog('적용 실패: ' + (e.response?.data?.detail || e.message)); }
  };

  const submit = async () => {
    if (!form.title.trim()) { alertDialog('제목을 입력하세요.'); return; }
    setSaveBusy(true);
    try {
      if (editingId) await ticketAPI.update(editingId, form);
      else await ticketAPI.create(form);
      setShowForm(false); setEditingId(null); setForm(empty); await load();
    } catch (e) { alertDialog('저장 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaveBusy(false); }
  };
  const openEdit = (t) => { setForm({ ...empty, ...t }); setEditingId(t.id); setShowForm(true); setActive(null); };
  const quickStatus = async (t, status) => {
    try {
      const r = await ticketAPI.update(t.id, { status });
      // 개발사(노션)에 반영이 안 됐으면 알린다 — 조용히 넘기면 저쪽도 바뀐 줄 안다.
      // 대표 원인은 개발사가 노션 페이지를 휴지통에 넣은 경우.
      if (r.data?.notion_sync_failed) {
        alertDialog('게시판 상태는 바꿨지만 개발사(노션)에는 반영하지 못했습니다. 노션 카드가 삭제됐거나 접근 권한이 바뀌었을 수 있습니다 — 노션에서 직접 확인해 주십시오.');
      }
      load();
      ticketAPI.get(t.id).then((g) => setActive((cur) => (cur && cur.id === t.id ? g.data : cur))).catch(() => {});
    } catch (e) { alertDialog('변경 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const sendReply = async (t) => {
    const text = replyText.trim();
    if (!text) return;
    setReplyBusy(true);
    try {
      await ticketAPI.reply(t.id, text);
      setReplyText('');
      const r = await ticketAPI.get(t.id);
      setActive((cur) => (cur && cur.id === t.id ? r.data : cur));
      alertDialog('메신저 스레드에 답변을 게시했습니다.');
    } catch (e) { alertDialog('답변 게시 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setReplyBusy(false); }
  };
  const remove = async (t) => { if (!(await confirmDialog('이 티켓을 삭제할까요?', { danger: true }))) return; try { await ticketAPI.remove(t.id); setActive(null); load(); } catch (e) { alertDialog('삭제 실패: ' + (e.response?.data?.detail || e.message)); } };

  // 입력 — 공용 input(바탕·글자색 없음). 폭은 쓰는 곳이 정한다.
  const Inp = { ...inputBase };

  return (
    <AppShell app="tickets" style={{ fontFamily: 'var(--gw-font)', background: 'var(--gw-bg, #fff)' }}>
      {handoffBusy && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--gw-overlay, #19191945)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--gw-surface, #fff)', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: '10px', padding: '25px', maxWidth: '86vw', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', boxShadow: SHADOW.float }}>
            <div style={{ width: '34px', height: '34px', border: '4px solid var(--gw-line, #eeeeec)', borderTopColor: 'var(--gw-blue, #2383e2)', borderRadius: '50%', animation: 'gw-handoff-spin 0.8s linear infinite' }} />
            <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>개발사 문의(Notion)로 이관 중입니다…</div>
            <div style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)' }}>추가 정보(캡처·파일)까지 함께 전달합니다. 첨부가 많으면 시간이 걸릴 수 있습니다.</div>
          </div>
          <style>{`@keyframes gw-handoff-spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}
      <div className="gw-tabbar-pad" style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        <Page>
          <PageHeader
            icon={IconTicket}
            tone="purple"
            title="CS 티켓"
            subtitle="화주·센터 문의를 티켓으로 통합 관리합니다. 시스템 사안은 개발사 문의(Notion)로 이관합니다. (메신저 자동승격은 연동 후)"
            actions={!active && view === 'tickets' ? <Button variant="primary" icon={IconPlus} onClick={() => { setEditingId(null); setForm(empty); setShowForm((v) => !v); }}>티켓 생성</Button> : null}
          />

          {isAdmin && (
            <div style={{ ...cardBase, padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gw-text, #32312e)' }}>Notion 이관 DB</span>
              <Pill tone={notionSet ? 'green' : 'red'}>{notionSet ? '설정됨' : '미설정'}</Pill>
              <input value={notionInput} onChange={(e) => setNotionInput(e.target.value)} placeholder="'개발사 문의' DB URL 또는 ID 붙여넣기" style={{ ...Inp, flex: 1, minWidth: '220px', width: 'auto', height: '30px' }} />
              <Button variant="secondary" onClick={saveNotion} disabled={notionBusy}>{notionBusy ? '저장 중...' : '저장'}</Button>
              <span style={{ fontSize: '11px', color: 'var(--gw-secondary, #64635f)', width: '100%', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconAlertTriangle size={13} stroke={ICON_STROKE} />Notion에서 해당 DB를 열어 통합 'AI게이트웨이'를 <b>연결(공유)</b>해야 쓰기가 됩니다.</span>
            </div>
          )}

          {/* 지표 6칸 — 공용 .gw-metrics 는 4칸 격자라 이 화면만 3열×2행(≤600 2열×3행)으로 덮는다 */}
          <style>{`.tk-metrics{grid-template-columns:repeat(3,minmax(0,1fr))!important;margin-bottom:18px!important}.tk-metrics .gw-metric:nth-child(3n){border-right:0}.tk-metrics .gw-metric:nth-child(-n+3){border-bottom:1px solid var(--gw-line)}@media (max-width:600px){.tk-metrics{grid-template-columns:repeat(2,minmax(0,1fr))!important}.tk-metrics .gw-metric:nth-child(n){border-right:1px solid var(--gw-line);border-bottom:1px solid var(--gw-line)}.tk-metrics .gw-metric:nth-child(2n){border-right:0}.tk-metrics .gw-metric:nth-child(n+5){border-bottom:0}}`}</style>
          <div className="gw-metrics tk-metrics">{[['전체', stats.total], ['접수', stats.open], ['처리중', stats.in_progress], ['완료', stats.done], ['긴급 미완', stats.urgent], ['개발사이관', stats.dev]].map(([label, v]) => <MetricTile key={label} label={label} value={v || 0} />)}</div>

          {!active && (
            <>
              {/* 티켓 ↔ 문의 이력 — 티켓은 '접수된 것'만이라 해결된 문의가 안 보였다(마스터 2026-08-18) */}
              <PillTabs
                ariaLabel="티켓 보기"
                idBase="tickets-view"
                value={view}
                onChange={setView}
                items={[{ key: 'tickets', label: '티켓', count: stats.total }, { key: 'intakes', label: '문의 이력(AI 1차답변)' }]}
              />
              <div role="tabpanel" id={`tickets-view-panel-${view}`} aria-labelledby={`tickets-view-tab-${view}`}>
              {view === 'intakes' ? (
                <IntakeList
                  intakes={intakes} counts={intakeCounts} loading={intakeLoading} error={intakeError}
                  fOutcome={fOutcome} setFOutcome={setFOutcome}
                  search={search} setSearch={setSearch}
                  open={openIntake} setOpen={setOpenIntake}
                  Inp={Inp} mobile={mobile} onReload={loadIntakes}
                  onOpenTicket={(id) => ticketAPI.get(id).then((r) => { setView('tickets'); setActive(r.data); }).catch(() => alertDialog('티켓을 불러오지 못했습니다.'))}
                />
              ) : (
              <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
                <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={{ ...Inp, width: 'auto', height: '30px' }}><option value="">상태 전체</option>{STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS[k].label}</option>)}</select>
                <select value={fHandling} onChange={(e) => setFHandling(e.target.value)} style={{ ...Inp, width: 'auto', height: '30px' }}><option value="">처리구분 전체</option><option value="self">자체처리</option><option value="dev">개발사이관</option></select>
                <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="검색" width={mobile ? '100%' : '200px'} />
                {hasFilter && <Button variant="quiet" onClick={() => { setFStatus(''); setFHandling(''); setSearch(''); }}>필터 초기화</Button>}
                <Button variant="secondary" icon={IconRefresh} title="목록·통계·열린 상세 새로고침" onClick={() => { load(); if (active) ticketAPI.get(active.id).then((r) => setActive(r.data)).catch(() => {}); }}
                  style={{ marginLeft: mobile ? 0 : 'auto', width: mobile ? '100%' : undefined, justifyContent: 'center' }}>새로고침</Button>
              </div>

              {showForm && <TicketForm form={form} setForm={setForm} Inp={Inp} onSubmit={submit} onCancel={() => { setShowForm(false); setEditingId(null); }} editing={!!editingId} saving={saveBusy} mobile={mobile} />}

              <div style={{ ...cardBase, padding: 0, overflow: 'hidden' }}>
                {loading && <LoadingState>티켓을 불러오는 중입니다.</LoadingState>}
                {!loading && error && (
                  <div style={{ padding: '14px' }}><ErrorState message={error} /><Button variant="secondary" size="sm" onClick={load} style={{ marginTop: '10px' }}>다시 불러오기</Button></div>
                )}
                {!loading && !error && tickets.length === 0 && <EmptyState description={hasFilter ? '조건에 맞는 티켓이 없습니다.' : '티켓이 없습니다.'} />}
                {!loading && !error && tickets.map((t, i) => (
                  <div key={t.id} onClick={() => { setAiSug(null); ticketAPI.get(t.id).then((r) => setActive(r.data)).catch(() => alertDialog('티켓을 불러오지 못했습니다.')); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderTop: i === 0 ? 'none' : '1px solid var(--gw-line, #eeeeec)', cursor: 'pointer', flexWrap: mobile ? 'wrap' : 'nowrap' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gw-subtle, #fbfbfa)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gw-muted, #92918d)', flexShrink: 0, minWidth: '36px' }}>#{t.id}</span>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: PRIO[t.priority]?.color, flexShrink: 0, width: '34px' }}>{PRIO[t.priority]?.label}</span>
                    <span style={{ flexShrink: 0 }}><Pill tone={STATUS[t.status]?.tone}>{STATUS[t.status]?.label}</Pill></span>
                    {/* 노션 원문 상태 — 우리 3종으로는 안 보이는 진행 단계(수정배포중·보류 등)를 그대로 보여준다.
                        접수/처리중/완료와 같은 뜻이면 중복이라 생략한다. */}
                    {t.notion_status && !['시작 전', '확인중', '종료'].includes(t.notion_status) && (
                      <span title="개발사(노션) 진행 상태" style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', flexShrink: 0 }}>· {t.notion_status}</span>
                    )}
                    {t.system && <span style={{ flexShrink: 0 }}><Tag tone="purple">{t.system}</Tag></span>}
                    <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 240px', minWidth: 0 }}>{t.title}</span>
                    {t.handling === 'dev' && <span style={{ fontSize: '11px', color: 'var(--gw-blue, #2383e2)', flexShrink: 0 }}>개발사</span>}
                    <span style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)', flexShrink: 0 }}>{t.shipper || t.center || ''}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0, fontSize: '12px', color: 'var(--gw-secondary, #64635f)' }}>{t.assignee_name && <Avatar name={t.assignee_name} size={19} />}{t.assignee_name || '미배정'}</span>
                    <span style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', flexShrink: 0, width: '78px', textAlign: 'right' }}>{fmtDate(t.created_at)}</span>
                  </div>
                ))}
              </div>
              </>
              )}
              </div>
            </>
          )}

          {active && (
            <div style={{ ...cardBase, padding: '22px' }}>
              <Button variant="quiet" size="sm" icon={IconArrowLeft} onClick={() => setActive(null)} style={{ marginBottom: '14px', marginLeft: '-8px' }}>목록</Button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', fontWeight: 500, color: PRIO[active.priority]?.color }}>{PRIO[active.priority]?.label}</span>
                <span style={{ flexShrink: 0 }}><Pill tone={STATUS[active.status]?.tone}>{STATUS[active.status]?.label}</Pill></span>
                {active.notion_status && (
                  <span title="개발사(노션) 진행 상태 — 노션에서 바뀌면 이 게시판도 따라갑니다"><Tag tone="gray">개발사: {active.notion_status}</Tag></span>
                )}
                {active.system && <Tag tone="purple">시스템 {active.system}</Tag>}
                <h2 style={{ fontSize: '19px', fontWeight: 620, color: 'var(--gw-text, #32312e)', margin: 0 }}><span style={{ color: 'var(--gw-muted, #92918d)', marginRight: '6px' }}>#{active.id}</span>{active.title}</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '8px', margin: '12px 0', fontSize: '13px' }}>
                {[['화주', active.shipper], ['센터', active.center], ['유형', active.type], ['담당자', active.assignee_name], ['처리구분', HANDLING[active.handling] || '-'], ['등록', active.created_by_name]].map(([k, v]) => (
                  <div key={k} style={{ background: 'var(--gw-gray-soft, #f1f1ef)', borderRadius: '8px', padding: '8px 10px' }}><span style={{ color: 'var(--gw-muted, #92918d)' }}>{k}</span><div style={{ color: 'var(--gw-text, #32312e)', fontWeight: 500 }}>{v || '-'}</div></div>
                ))}
              </div>
              {safeUrl(active.notion_url) && <a href={safeUrl(active.notion_url)} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: 'var(--gw-blue, #2383e2)', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}><IconExternalLink size={15} stroke={ICON_STROKE} />Notion 이관 링크</a>}
              <div style={{ fontSize: '14px', color: 'var(--gw-text, #32312e)', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: '14px 0', padding: '14px 0', borderTop: '1px solid var(--gw-line, #eeeeec)', borderBottom: '1px solid var(--gw-line, #eeeeec)', minHeight: '60px' }}>{active.content || ' '}</div>

              {active.ai_summary && (
                <div style={{ background: 'var(--gw-blue-soft, #eaf3fc)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '8px', padding: '12px 14px', margin: '0 0 12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 620, color: 'var(--gw-blue-ink, #356c9b)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}><IconRobot size={15} stroke={ICON_STROKE} />AI 정리 (개발사 전달용)</div>
                  <div style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}><Markdown compact>{active.ai_summary}</Markdown></div>
                </div>
              )}
              {aiSug && (
                <div style={{ ...cardBase, padding: '10px 14px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px' }}>
                  <span style={{ fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>AI 제안</span>
                  <span>처리: <b>{HANDLING[aiSug.handling] || '-'}</b></span>
                  <span>우선순위: <b>{PRIO[aiSug.priority]?.label || '-'}</b></span>
                  {aiSug.type && <span>유형: <b>{aiSug.type}</b></span>}
                  {aiSug.shipper && <span>화주: <b>{aiSug.shipper}</b></span>}
                  {aiSug.center && <span>센터: <b>{aiSug.center}</b></span>}
                  <Button variant="primary" size="sm" onClick={() => applyAi(active)} style={{ marginLeft: 'auto' }}>적용</Button>
                  <Button variant="quiet" size="sm" onClick={() => setAiSug(null)}>닫기</Button>
                </div>
              )}

              {(active.followups || []).length > 0 && (
                <div style={{ background: 'var(--gw-subtle, #fbfbfa)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '8px', padding: '12px 14px', margin: '0 0 12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 620, color: 'var(--gw-text, #32312e)', marginBottom: '4px' }}>💬 메신저 스레드 기록 ({active.followups.length}) — 추가 정보·답변</div>
                  {active.followups.map((u) => {
                    const K = u.kind === 'ax' ? { label: 'AX실 답변', tone: 'blue' }
                      : u.kind === 'partner' ? { label: '개발사', tone: 'yellow' }
                      : u.kind === 'notion' ? { label: '노션 댓글', tone: 'purple' }
                      : { label: '문의자', tone: 'gray' };
                    return (
                      <div key={u.id} style={{ padding: '8px 0', borderTop: '1px solid var(--gw-line, #eeeeec)' }}>
                        <div style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Tag tone={K.tone}>{K.label}</Tag>
                          {fmtDate(u.created_at)} · {u.author || '직원'}
                        </div>
                        {u.message && <div style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', whiteSpace: 'pre-wrap', marginBottom: (u.files || []).length ? '6px' : 0 }}>{u.message}</div>}
                        {(u.files || []).length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {u.files.map((f) => <MmAttachment key={f.id} tid={active.id} f={f} />)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {active.mm_root_id && (
                <div style={{ ...cardBase, padding: '12px 14px', margin: '0 0 12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 620, color: 'var(--gw-text, #32312e)', marginBottom: '8px' }}>💬 메신저 스레드 답변 (접수자에게 바로 전달됩니다)</div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
                    <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2}
                      placeholder="확인 결과·조치 방법을 입력하면 메신저 원 스레드에 [AX실 답변]으로 게시됩니다"
                      style={{ ...Inp, flex: 1, width: mobile ? '100%' : undefined, resize: 'vertical' }} />
                    <Button variant="primary" onClick={() => sendReply(active)} disabled={replyBusy || !replyText.trim()} style={{ whiteSpace: 'nowrap' }}>
                      {replyBusy ? '게시 중…' : '스레드에 답변'}
                    </Button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)' }}>상태 변경:</span>
                {STATUS_KEYS.map((k) => <Button key={k} size="sm" variant={active.status === k ? 'primary' : 'secondary'} onClick={() => quickStatus(active, k)}>{STATUS[k].label}</Button>)}
                <span style={{ marginLeft: mobile ? 0 : 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap', width: mobile ? '100%' : undefined }}>
                  <Button variant="secondary" size="sm" icon={aiBusy ? undefined : IconRobot} onClick={() => runAi(active)} disabled={aiBusy} title="AI가 분류·우선순위·구조화 정리">{aiBusy ? '분석 중…' : 'AI 분석'}</Button>
                  {active.notion_url ? (
                    <a href={safeUrl(active.notion_url)} target="_blank" rel="noreferrer" title="이미 개발사 문의(Notion)로 이관됨 — 클릭하면 Notion 페이지 열기"
                      className="gw-btn gw-btn--secondary"
                      style={{ ...btn('secondary', 'sm'), display: 'inline-flex', alignItems: 'center', gap: '5px', textDecoration: 'none', color: 'var(--gw-green-ink, #426b55)' }}>
                      ✔ 개발사 이관됨 <IconExternalLink size={14} stroke={ICON_STROKE} />
                    </a>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => handoff(active)} disabled={handoffBusy} title="개발사 문의(Notion)로 이관" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>{handoffBusy ? '이관 중…' : <>개발사 이관 <IconExternalLink size={14} stroke={ICON_STROKE} /></>}</Button>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => openEdit(active)}>수정</Button>
                  {(active.created_by === me.id || me.role === 'admin') && <Button variant="danger" size="sm" onClick={() => remove(active)}>삭제</Button>}
                </span>
              </div>
            </div>
          )}
        </Page>
      </div>
    </AppShell>
  );
}

function TicketForm({ form, setForm, Inp, onSubmit, onCancel, editing, saving, mobile }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div style={{ ...cardBase, padding: '18px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <input value={form.title} onChange={set('title')} placeholder="제목" style={{ ...Inp, width: '100%' }} autoFocus />
      <textarea value={form.content} onChange={set('content')} placeholder="내용(증상·요청)" rows={4} style={{ ...Inp, width: '100%', resize: 'vertical' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '8px' }}>
        <input value={form.shipper} onChange={set('shipper')} placeholder="화주" style={Inp} />
        <select value={form.system || ''} onChange={set('system')} style={Inp}>
          <option value="">시스템 구분</option><option value="1.0">1.0</option><option value="2.0">2.0</option>
        </select>
        <input value={form.type} onChange={set('type')} placeholder="유형" style={Inp} />
        <select value={form.priority} onChange={set('priority')} style={Inp}>{PRIO_KEYS.map((k) => <option key={k} value={k}>{PRIO[k].label}</option>)}</select>
        <select value={form.handling} onChange={set('handling')} style={Inp}><option value="">처리구분</option><option value="self">자체처리</option><option value="dev">개발사이관</option></select>
        {editing && <select value={form.status} onChange={set('status')} style={Inp}>{STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS[k].label}</option>)}</select>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
        <Button variant="secondary" onClick={onCancel} disabled={saving} style={{ flex: mobile ? '1 1 120px' : undefined }}>취소</Button>
        <Button variant="primary" onClick={onSubmit} disabled={saving} style={{ flex: mobile ? '1 1 120px' : undefined }}>{saving ? '저장 중...' : (editing ? '수정' : '생성')}</Button>
      </div>
    </div>
  );
}
